import { Avatar, Typography } from '@mui/material';
import Link from 'next/link';
import { CatalogueSeller } from 'src/logic/models/catalogue/catalogue-content';
import { getAvatarDisplayName } from 'src/logic/utils/avatar';
import { formatInteger } from 'src/logic/utils/numbers';
import { sm } from 'src/site-map';
import { companyAvatarStyles } from 'src/ui/components/dumb/company-details/shared/styles';
import InfoPopperList from './components/InfoPopperList';
import Label from './components/Label';
import VerticalDivider from './components/VerticalDivider';
import { useQuery } from '@tanstack/react-query';
import { getSellerDetails } from 'src/logic/services/catalogue/sellers';
import { SellersDetails } from 'src/logic/models/catalogue/CatalogueIndex';
import { LoadingButton } from '@mui/lab';

const SellerBanner = ({ seller }: { seller: CatalogueSeller }) => {
  const sellerId = seller?.seller?.id;

  const { data: sellerDetails, isLoading } = useQuery<SellersDetails>({
    queryKey: ['SELLER_BANNER', sellerId],
    queryFn: async () => {
      const res = await getSellerDetails(sellerId);
      return res.data;
    },
    enabled: sellerId !== undefined && sellerId !== null,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: Infinity,
    cacheTime: Infinity,
    retry: 0,
  });

  return (
    <div
      style={{
        marginBlock: '1rem',
        backgroundColor: '#fff',
        borderRadius: '8px',
        padding: '.75rem 1rem',
        width: '100%',
        display: 'flex',
        gap: '1rem',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
        <Avatar sx={{ ...companyAvatarStyles, padding: '1.75em', fontSize: 14 }}>
          {isLoading ? (
            <LoadingButton loading={true} loadingPosition='center' />
          ) : (
            getAvatarDisplayName(sellerDetails?.company?.name)
          )}
        </Avatar>

        {isLoading ? (
          <LoadingButton loading={true} loadingPosition='start' />
        ) : (
          <Link href={sm.catalogue.sellers.sellerId(`${seller.seller.id}`).url}>
            <Typography
              color='#3A3E50'
              fontWeight={700}
              fontSize={18}
              className='cursor-pointer underline-on-hover'
              noWrap
            // maxWidth={'300px'}
            >
              {sellerDetails?.company?.name}
            </Typography>
          </Link>
        )}
      </div>

      <>
        <VerticalDivider />
        <div>
          <Label label='Location' />
          {isLoading ? (
            <LoadingButton loading={true} loadingPosition='start' />
          ) : (
            <Typography color='#3A3E50' fontSize={14} fontWeight={600}>
              {sellerDetails?.company?.city?.name} {sellerDetails?.company?.country?.name}
            </Typography>
          )}
        </div>
      </>

      <>
        <VerticalDivider />
        <div>
          <Label label='Products & Services' />
          {isLoading ? (
            <LoadingButton loading={true} loadingPosition='start' />
          ) : (
            <Typography color='#3A3E50' fontSize={14} fontWeight={600}>
              {formatInteger(sellerDetails?.company?.number_of_products + sellerDetails?.company?.number_of_services)} Products & Services
            </Typography>
          )}
        </div>
      </>

      <>
        <VerticalDivider />
        <div>
          <Label label='Categories' />
          {isLoading ? (
            <LoadingButton loading={true} loadingPosition='start' />
          ) : (
            <Typography color='#3A3E50' fontSize={14} fontWeight={600}>
              {formatInteger(sellerDetails?.company?.categories?.length || 0)} {sellerDetails?.company?.categories?.length !== 1 ? 'Categories' : 'Category'} {' '}
              {sellerDetails?.company?.categories && sellerDetails?.company?.categories?.length > 0 && (
                <InfoPopperList
                  title={sellerDetails?.company?.categories?.length !== 1 ? 'Categories' : 'Category'}
                  items={sellerDetails?.company?.categories}
                  renderItem={item => {
                    return (
                      <Typography key={item.id} color='#3A3E50' fontWeight={400} fontSize={12} lineHeight={1} my={1}>
                        {item.name}
                      </Typography>
                    );
                  }}
                />
              )}
            </Typography>
          )}
        </div>
      </>

      <>
        <VerticalDivider />
        <div>
          <Label label='Brands' />
          {isLoading ? (
            <LoadingButton loading={true} loadingPosition='start' />
          ) : (
            <Typography color='#3A3E50' fontSize={14} fontWeight={600}>
              {formatInteger(sellerDetails?.company?.brands?.length || 0)} Brand{sellerDetails?.company?.brands?.length !== 1 && 's'}{' '}
              {sellerDetails?.company?.brands && sellerDetails?.company?.brands.length > 0 && (
                <InfoPopperList
                  title={sellerDetails?.company?.brands?.length !== 1 ? 'Brands' : 'Brands'}
                  items={sellerDetails?.company?.brands}
                  renderItem={item => {
                    return (
                      <Typography key={item.id} color='#3A3E50' fontWeight={400} fontSize={12} lineHeight={1} my={1}>
                        {item.name}
                      </Typography>
                    );
                  }}
                />
              )}
            </Typography>
          )}
        </div>
      </>

      <div>
      </div>
    </div>
  );
};

export default SellerBanner;
